// =============================================================================
// FORM BUILDER TYPES - Professional form builder like SurveyJS
// =============================================================================

export type FieldType =
  // Basic inputs
  | 'text'
  | 'email'
  | 'number'
  | 'phone'
  | 'url'
  | 'password'
  | 'textarea'
  // Selection
  | 'select'
  | 'multiselect'
  | 'checkbox'
  | 'checkboxgroup'
  | 'radio'
  | 'boolean'
  // Date/Time
  | 'date'
  | 'time'
  | 'datetime'
  // Advanced
  | 'file'
  | 'image'
  | 'signature'
  | 'rating'
  | 'nps'
  | 'slider'
  | 'ranking'
  | 'matrix'
  // Display
  | 'html'
  | 'heading'
  | 'paragraph'
  | 'image-display'
  // Layout
  | 'section'
  | 'panel'
  | 'divider';

export interface FieldOption {
  label: string;
  value: string;
  imageUrl?: string;
}

export interface MatrixRow {
  value: string;
  label: string;
}

export interface MatrixColumn {
  value: string;
  label: string;
}

export interface ValidationRule {
  type: 'required' | 'minLength' | 'maxLength' | 'min' | 'max' | 'pattern' | 'email' | 'url' | 'custom';
  value?: string | number;
  message: string;
}

export interface ConditionalLogic {
  fieldId: string;
  operator: 'equals' | 'notEquals' | 'contains' | 'notContains' | 'isEmpty' | 'isNotEmpty' | 'greaterThan' | 'lessThan';
  value?: unknown;
  action: 'show' | 'hide' | 'enable' | 'disable' | 'require';
}

export interface FormField {
  id: string;
  type: FieldType;
  name: string;
  label: string;
  placeholder?: string;
  helpText?: string;
  description?: string;
  defaultValue?: unknown;
  options?: FieldOption[];
  validation: ValidationRule[];
  width: 'full' | 'half' | 'third' | 'quarter';
  // Advanced options
  readOnly?: boolean;
  hidden?: boolean;
  // Conditional logic
  conditions?: ConditionalLogic[];
  // Type-specific properties
  minValue?: number;
  maxValue?: number;
  step?: number;
  rows?: number;
  cols?: number;
  maxFiles?: number;
  maxFileSize?: number;
  acceptedFileTypes?: string[];
  // Matrix specific
  matrixRows?: MatrixRow[];
  matrixColumns?: MatrixColumn[];
  // Rating/NPS specific
  ratingCount?: number;
  // Slider specific
  showValue?: boolean;
  // HTML specific
  htmlContent?: string;
  // Heading specific
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4';
}

export interface FormSection {
  id: string;
  title?: string;
  description?: string;
  fields: FormField[];
  collapsed?: boolean;
  collapsible?: boolean;
}

export interface FormPage {
  id: string;
  title: string;
  description?: string;
  sections: FormSection[];
}

export interface FormTheme {
  primaryColor: string;
  backgroundColor: string;
  textColor: string;
  borderRadius: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  fontSize: 'sm' | 'md' | 'lg';
  spacing: 'compact' | 'normal' | 'relaxed';
}

export interface Form {
  id: string;
  name: string;
  description: string | undefined;
  sections: FormSection[];
  pages?: FormPage[];
  settings: FormSettings;
  theme: FormTheme;
  createdAt: string;
  updatedAt: string;
}

export interface FormSettings {
  submitLabel: string;
  resetLabel?: string;
  successMessage: string;
  redirectUrl?: string;
  allowMultipleSubmissions: boolean;
  showProgressBar: boolean;
  showPageNumbers: boolean;
  showRequiredAsterisk: boolean;
  layout: 'default' | 'card' | 'minimal' | 'bordered';
  labelPosition: 'top' | 'left' | 'floating';
}

// Field templates catalog
export interface FieldTemplate {
  type: FieldType;
  label: string;
  labelFr: string;
  icon: string;
  description: string;
  descriptionFr: string;
  category: 'basic' | 'selection' | 'datetime' | 'advanced' | 'display' | 'layout';
  defaultConfig: Partial<FormField>;
}

export const FIELD_TEMPLATES: FieldTemplate[] = [
  // === BASIC FIELDS ===
  {
    type: 'text',
    label: 'Text',
    labelFr: 'Texte',
    icon: 'Type',
    description: 'Single line text input',
    descriptionFr: 'Champ texte simple',
    category: 'basic',
    defaultConfig: { placeholder: 'Saisissez votre texte...' },
  },
  {
    type: 'email',
    label: 'Email',
    labelFr: 'Email',
    icon: 'Mail',
    description: 'Email address input',
    descriptionFr: 'Adresse email',
    category: 'basic',
    defaultConfig: {
      placeholder: 'email@exemple.com',
      validation: [{ type: 'email', message: 'Veuillez saisir un email valide' }],
    },
  },
  {
    type: 'number',
    label: 'Number',
    labelFr: 'Nombre',
    icon: 'Hash',
    description: 'Numeric input',
    descriptionFr: 'Champ numérique',
    category: 'basic',
    defaultConfig: { placeholder: '0' },
  },
  {
    type: 'phone',
    label: 'Phone',
    labelFr: 'Téléphone',
    icon: 'Phone',
    description: 'Phone number input',
    descriptionFr: 'Numéro de téléphone',
    category: 'basic',
    defaultConfig: { placeholder: '+33 6 00 00 00 00' },
  },
  {
    type: 'url',
    label: 'URL',
    labelFr: 'URL',
    icon: 'Link',
    description: 'Website URL input',
    descriptionFr: 'Adresse de site web',
    category: 'basic',
    defaultConfig: { placeholder: 'https://...' },
  },
  {
    type: 'textarea',
    label: 'Long Text',
    labelFr: 'Texte long',
    icon: 'AlignLeft',
    description: 'Multi-line text area',
    descriptionFr: 'Zone de texte multiligne',
    category: 'basic',
    defaultConfig: { placeholder: 'Saisissez votre message...', rows: 4 },
  },
  {
    type: 'password',
    label: 'Password',
    labelFr: 'Mot de passe',
    icon: 'Lock',
    description: 'Secure password input',
    descriptionFr: 'Champ mot de passe',
    category: 'basic',
    defaultConfig: { placeholder: '••••••••' },
  },

  // === SELECTION FIELDS ===
  {
    type: 'select',
    label: 'Dropdown',
    labelFr: 'Liste déroulante',
    icon: 'ChevronDown',
    description: 'Single selection dropdown',
    descriptionFr: 'Sélection unique',
    category: 'selection',
    defaultConfig: {
      placeholder: 'Sélectionnez...',
      options: [
        { label: 'Option 1', value: 'option1' },
        { label: 'Option 2', value: 'option2' },
        { label: 'Option 3', value: 'option3' },
      ],
    },
  },
  {
    type: 'multiselect',
    label: 'Multi Select',
    labelFr: 'Sélection multiple',
    icon: 'ListChecks',
    description: 'Multiple selection dropdown',
    descriptionFr: 'Sélection multiple',
    category: 'selection',
    defaultConfig: {
      options: [
        { label: 'Option 1', value: 'option1' },
        { label: 'Option 2', value: 'option2' },
        { label: 'Option 3', value: 'option3' },
      ],
    },
  },
  {
    type: 'radio',
    label: 'Radio Group',
    labelFr: 'Boutons radio',
    icon: 'CircleDot',
    description: 'Single choice radio buttons',
    descriptionFr: 'Choix unique avec boutons',
    category: 'selection',
    defaultConfig: {
      options: [
        { label: 'Option 1', value: 'option1' },
        { label: 'Option 2', value: 'option2' },
        { label: 'Option 3', value: 'option3' },
      ],
    },
  },
  {
    type: 'checkbox',
    label: 'Checkbox',
    labelFr: 'Case à cocher',
    icon: 'CheckSquare',
    description: 'Single checkbox',
    descriptionFr: 'Case à cocher unique',
    category: 'selection',
    defaultConfig: {},
  },
  {
    type: 'checkboxgroup',
    label: 'Checkbox Group',
    labelFr: 'Groupe de cases',
    icon: 'CheckSquare',
    description: 'Multiple checkboxes',
    descriptionFr: 'Cases à cocher multiples',
    category: 'selection',
    defaultConfig: {
      options: [
        { label: 'Option 1', value: 'option1' },
        { label: 'Option 2', value: 'option2' },
        { label: 'Option 3', value: 'option3' },
      ],
    },
  },
  {
    type: 'boolean',
    label: 'Yes/No Toggle',
    labelFr: 'Oui/Non',
    icon: 'ToggleLeft',
    description: 'Boolean toggle switch',
    descriptionFr: 'Interrupteur Oui/Non',
    category: 'selection',
    defaultConfig: {},
  },

  // === DATE/TIME FIELDS ===
  {
    type: 'date',
    label: 'Date',
    labelFr: 'Date',
    icon: 'Calendar',
    description: 'Date picker',
    descriptionFr: 'Sélecteur de date',
    category: 'datetime',
    defaultConfig: {},
  },
  {
    type: 'time',
    label: 'Time',
    labelFr: 'Heure',
    icon: 'Clock',
    description: 'Time picker',
    descriptionFr: 'Sélecteur d\'heure',
    category: 'datetime',
    defaultConfig: {},
  },
  {
    type: 'datetime',
    label: 'Date & Time',
    labelFr: 'Date et heure',
    icon: 'CalendarClock',
    description: 'Date and time picker',
    descriptionFr: 'Sélecteur date et heure',
    category: 'datetime',
    defaultConfig: {},
  },

  // === ADVANCED FIELDS ===
  {
    type: 'file',
    label: 'File Upload',
    labelFr: 'Téléchargement',
    icon: 'Upload',
    description: 'File upload field',
    descriptionFr: 'Téléchargement de fichiers',
    category: 'advanced',
    defaultConfig: { maxFiles: 1, maxFileSize: 10 },
  },
  {
    type: 'image',
    label: 'Image Upload',
    labelFr: 'Image',
    icon: 'ImagePlus',
    description: 'Image upload with preview',
    descriptionFr: 'Téléchargement d\'image',
    category: 'advanced',
    defaultConfig: { acceptedFileTypes: ['image/*'] },
  },
  {
    type: 'signature',
    label: 'Signature',
    labelFr: 'Signature',
    icon: 'PenTool',
    description: 'Digital signature pad',
    descriptionFr: 'Signature numérique',
    category: 'advanced',
    defaultConfig: {},
  },
  {
    type: 'rating',
    label: 'Star Rating',
    labelFr: 'Étoiles',
    icon: 'Star',
    description: 'Star rating 1-5',
    descriptionFr: 'Note en étoiles',
    category: 'advanced',
    defaultConfig: { ratingCount: 5 },
  },
  {
    type: 'nps',
    label: 'NPS Score',
    labelFr: 'Score NPS',
    icon: 'Gauge',
    description: 'Net Promoter Score 0-10',
    descriptionFr: 'Score de recommandation',
    category: 'advanced',
    defaultConfig: {},
  },
  {
    type: 'slider',
    label: 'Slider',
    labelFr: 'Curseur',
    icon: 'SlidersHorizontal',
    description: 'Range slider',
    descriptionFr: 'Curseur de valeur',
    category: 'advanced',
    defaultConfig: { minValue: 0, maxValue: 100, step: 1, showValue: true },
  },
  {
    type: 'ranking',
    label: 'Ranking',
    labelFr: 'Classement',
    icon: 'ListOrdered',
    description: 'Drag to rank items',
    descriptionFr: 'Classement par glisser-déposer',
    category: 'advanced',
    defaultConfig: {
      options: [
        { label: 'Item 1', value: 'item1' },
        { label: 'Item 2', value: 'item2' },
        { label: 'Item 3', value: 'item3' },
      ],
    },
  },
  {
    type: 'matrix',
    label: 'Matrix',
    labelFr: 'Matrice',
    icon: 'Grid3X3',
    description: 'Matrix/table question',
    descriptionFr: 'Question en tableau',
    category: 'advanced',
    defaultConfig: {
      matrixRows: [
        { value: 'row1', label: 'Ligne 1' },
        { value: 'row2', label: 'Ligne 2' },
      ],
      matrixColumns: [
        { value: 'col1', label: 'Très satisfait' },
        { value: 'col2', label: 'Satisfait' },
        { value: 'col3', label: 'Neutre' },
        { value: 'col4', label: 'Insatisfait' },
      ],
    },
  },

  // === DISPLAY ELEMENTS ===
  {
    type: 'heading',
    label: 'Heading',
    labelFr: 'Titre',
    icon: 'Heading',
    description: 'Section heading',
    descriptionFr: 'Titre de section',
    category: 'display',
    defaultConfig: { headingLevel: 'h2' },
  },
  {
    type: 'paragraph',
    label: 'Paragraph',
    labelFr: 'Paragraphe',
    icon: 'Text',
    description: 'Descriptive text',
    descriptionFr: 'Texte descriptif',
    category: 'display',
    defaultConfig: {},
  },
  {
    type: 'html',
    label: 'HTML',
    labelFr: 'HTML',
    icon: 'Code',
    description: 'Custom HTML content',
    descriptionFr: 'Contenu HTML personnalisé',
    category: 'display',
    defaultConfig: { htmlContent: '<p>Contenu personnalisé</p>' },
  },
  {
    type: 'image-display',
    label: 'Display Image',
    labelFr: 'Afficher image',
    icon: 'Image',
    description: 'Display an image',
    descriptionFr: 'Afficher une image',
    category: 'display',
    defaultConfig: {},
  },

  // === LAYOUT ELEMENTS ===
  {
    type: 'section',
    label: 'Section',
    labelFr: 'Section',
    icon: 'LayoutTemplate',
    description: 'Group related fields',
    descriptionFr: 'Grouper les champs',
    category: 'layout',
    defaultConfig: {},
  },
  {
    type: 'panel',
    label: 'Panel',
    labelFr: 'Panneau',
    icon: 'PanelTop',
    description: 'Collapsible panel',
    descriptionFr: 'Panneau repliable',
    category: 'layout',
    defaultConfig: {},
  },
  {
    type: 'divider',
    label: 'Divider',
    labelFr: 'Séparateur',
    icon: 'Minus',
    description: 'Horizontal divider',
    descriptionFr: 'Ligne de séparation',
    category: 'layout',
    defaultConfig: {},
  },
];

export const CATEGORY_LABELS: Record<string, { en: string; fr: string }> = {
  basic: { en: 'Basic Inputs', fr: 'Champs de base' },
  selection: { en: 'Selection', fr: 'Sélection' },
  datetime: { en: 'Date & Time', fr: 'Date et heure' },
  advanced: { en: 'Advanced', fr: 'Avancé' },
  display: { en: 'Display', fr: 'Affichage' },
  layout: { en: 'Layout', fr: 'Mise en page' },
};

export const DEFAULT_THEME: FormTheme = {
  primaryColor: '#3B82F6',
  backgroundColor: '#FFFFFF',
  textColor: '#1F2937',
  borderRadius: 'md',
  fontSize: 'md',
  spacing: 'normal',
};
